import { createStore } from 'vuex';

import modules from './modules';
import createPersistedState from 'vuex-persist';
import axios from 'axios';

const persistedState = new createPersistedState({
  key: 'crm_vuex',
  storage: localStorage,
  reducer: state => ({
    userId: state.userId,
    salesOfficerDetails: state.salesOfficerDetails,
    csrData2: state.csrData2,
    airportCodesData2: state.airportCodesData2,
    uomData2: state.uomData2,
    truckTypeData2: state.truckTypeData2,
    incotermData: state.incotermData,
    rateTypesData: state.rateTypesData,
    commodityData: state.commodityData,
    customersData2: state.customersData2,
    tripTypeData: state.tripTypeData,
    airlineData2: state.airlineData2,
    wtbData2: state.wtbData2,
    containerTypeData2: state.containerTypeData2,
    transTypeData2: state.transTypeData2,
    loadTypeData2: state.loadTypeData2,
    csrConversionData: state.csrConversionData,
  })
});

const store = createStore({
  modules,
  // Enable strict mode in development to get a warning
  // when mutating state outside of a mutation.
  // https://vuex.vuejs.org/guide/strict.html
  strict: process.env.NODE_ENV !== 'production',
  devtools: false,
  state: {
    menus: [],
    userId: null,
    salesOfficerDetails: {},
    loadMore: [],

    ouAccess: [],
    userRoles: [],
    activeOU: '',
    origDestAirData: [],
    origDestDomesticData: [],
    origDestSeaData: [],
    commodityData: [],
    serviceData: [],
    customerData: [],
    consigneeData: [],
    shipperData: [],
    incotermData: [],
    currencyData: [],
    satellitesData: [],
    stationsData: [],
    agentData: [],
    wtbData: [],
    unitmeasureData: [],
    airlineData: [],
    rateTypesData: [],
    uomData: [],
    designationData: [],
    areaData: [],
    citiesData: [],
    airlineClassData: [],
    truckTypeData: [],
    departmentData: [],
    tripTypeData: [],
    containerTypeData: [],
    warehousesData: [],
    warehouseData: [],
    companiesData: [],
    countryData: [],

    // Booking v2 state
    csrData: [],
    csrData2: [],
    airportCodesData2: [],
    uomData2: [],
    truckTypeData2: [],
    customersData2: [],
    currenciesData2: [],
    chargeGroupsData2: [],
    chargeListData2: [],
    airlineData2: [],
    wtbData2: [],
    containerTypeData2: [],
    transTypeData2: [],
    loadTypeData2: [],

    // Ocean freight rates maintenance (shared across the list, form dialog, and import grid)
    shippingLineOptions: [],
    containerTypeOptions: [],

    activeBookingId: sessionStorage.getItem('activeBookingId') || null,
    csrConversionData: null,
    airlineSurchargesIsProcessed: false,
    seaSurchargesIsProcessed: false,
  },
  actions: {
    setUserId(context, Id) {
      context.commit('setUserId', Id);
    },
    setSalesOfficerDetails(context, data) {
      context.commit('setSalesOfficerDetails', data);
    },
    addLoadMore(context, data) {
      context.commit('addLoadMore', data);
    },
    removeLoadMore(context, data) {
      context.commit('removeLoadMore', data);
    },
    // preloaded Maintenance Datas used in CSR selections
    getSatellites(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/csr_get_satellite`,
          params: params
        }).then(res => {
          context.commit('setSatellites', res.data.data);
        }).catch((error) => {
          console.error('Error fetching data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating data:', error);
      }
    },
    getDepartmentList(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_departments`,
          params: params
        }).then(res => {
          context.commit('setDepartmentList', res.data.data);
        }).catch((error) => {
          console.error('Error fetching data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating data:', error);
      }
    },
    getTruckTypeList(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_truck_type_list`,
          params: params
        }).then(res => {
          context.commit('setTruckTypeList', res.data.data);
        }).catch((error) => {
          console.error('Error fetching data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating data:', error);
      }
    },
    getCities(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_city`,
          params: params
        }).then(res => {
          context.commit('setCities', res.data.data);
        }).catch((error) => {
          console.error('Error fetching cities:', error);
          return null;
        });
      } catch (error) {
        console.error('Error fetching cities:', error);
      }
    },
    getAirlineClass(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_airline_class`,
          params: params
        }).then(res => {
          context.commit('setAirlineClass', res.data.data);
        }).catch((error) => {
          console.error('Error fetching data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating data:', error);
      }
    },
    getAreaData(context) {
      axios.get(`${process.env.VUE_APP_AUTHDOMAIN}/api/v1/getAreaCode`)
        .then(res => {
          context.commit('setAreaData', res.data.data);
        })
        .catch(err => {
          console.error('Error loading data:', err);
        });
    },
    getTripType(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_trip_type`,
          params: params
        }).then(res => {
          context.commit('setTripType', res.data.data);
        }).catch((error) => {
          console.error('Error fetching trip type data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error in getTripType action:', error);
      }
    },
    getDesignation(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_designations`,
          params: params
        }).then(res => {
          context.commit('setDesignation', res.data.data);
        }).catch((error) => {
          console.error('Error fetching data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating data:', error);
      }
    },
    getOrigDestAir(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_orig_dest_air`,
          params: params
        }).then(res => {
          context.commit('setOrigDestAir', res.data.data);
        }).catch((error) => {
          console.error('Error fetching data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating data:', error);
      }
    },
    getUOM(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/oum_list`,
          params: params
        }).then(res => {
          context.commit('setUOM', res.data.data);
        }).catch((error) => {
          console.error('Error fetching data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating data:', error);
      }
    },
    getRateTypes(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/rate_type_list`,
          params: params
        }).then(res => {
          context.commit('setRateTypes', res.data);
        }).catch((error) => {
          console.error('Error fetching data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating data:', error);
      }
    },
    getOrigDestDomestic(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/domestics`,
          params: params
        }).then(res => {
          context.commit('setOrigDestDomestic', res.data.data);
        }).catch((error) => {
          console.error('Error fetching data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating data:', error);
      }
    },
    getOrigDestSea(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_orig_dest_sea`,
          params: params
        }).then(res => {
          context.commit('setOrigDestSea', res.data.data);
        }).catch((error) => {
          console.error('Error fetching sea data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating sea data:', error);
      }
    },
    getCurrencyData(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_currency`,
          params: params
        }).then(res => {
          context.commit('setCurrencyData', res.data.data);
        }).catch((error) => {
          console.error('Error fetching data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating data:', error);
      }
    },
    getCommodity(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_commodity`,
          params: params
        }).then(res => {
          context.commit('setCommodity', res.data.data);
        }).catch((error) => {
          console.error('Error fetching data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating data:', error);
      }
      // context.commit('setCommodity',params)
    },
    getService(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_services`,
          params: params
        }).then(res => {
          context.commit('setService', res.data.data);
        }).catch((error) => {
          console.error('Error fetching data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating data:', error);
      }
    },
    async getCustomer(context, params = {}) {
      // if(params.customer_type_include == "customer"){
      //     context.commit('addLoadMore','customerData')
      // }
      try {
        await axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_customer`,
          params: params
        }).then(res => {

          if (params.customer_type_include == 'customer') {
            context.commit('setCustomer', res.data.data);
          }
          if (params.customer_type_include == 'consignee') {
            context.commit('setConsignee', res.data.data);
          }
          if (params.customer_type_include == 'shipper') {
            context.commit('setShipper', res.data.data);
          }
          if (params.customer_type_include == 'agent') {
            context.commit('setAgent', res.data.data);
          }
        }).catch((error) => {
          console.error('Error fetching customers:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating customers:', error);
      }
    },
    getIncoterm(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_incoterms`,
          params: params
        }).then(res => {
          context.commit('setIncoterm', res.data.data);
        }).catch((error) => {
          console.error('Error fetching data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating data:', error);
      }
    },
    getWtb(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_weight_break`,
          params: params
        }).then(res => {
          context.commit('setWtb', res.data.data);
        }).catch((error) => {
          console.error('Error fetching data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating data:', error);
      }
    },
    getAirline(context) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_airline`,
          params: {
          }
        }).then(res => {
          context.commit('setAirline', res.data.data);
        }).catch((error) => {
          console.error('Error fetching data:', error);
          return null;
        });
      } catch (error) {
        console.error('Error updating data:', error);
      }
    },
    getCompanies(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_companies`,
          params: params,
        }).then(res => {
          context.commit('setCompanies', res.data.data);
        });
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    },
    getWarehouses(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_warehouse`,
          params: params,
        }).then(res => {
          context.commit('setWarehouses', res.data.data);
        });
      } catch (error) {
        console.error('Error fetching warehouses:', error);
      }
    },
    getChargeHeaders(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/charges-headers`,
          params: params,
        }).then(res => {
          context.commit('setChargeHeaders', res.data.data);
        });
      } catch (error) {
        console.error('Error fetching charge headers:', error);
      }
    },
    getPeriods(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_periods`,
          params: params,
        }).then(res => {
          context.commit('setPeriods', res.data.data);
        });
      } catch (error) {
        console.error('Error fetching periods:', error);
      }
    },
    getRates(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_rates`,
          params: params,
        }).then(res => {
          context.commit('setRates', res.data.data);
        });
      } catch (error) {
        console.error('Error fetching rates:', error);
      }
    },
    getUoms(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_uoms`,
          params: params,
        }).then(res => {
          context.commit('setUomsData', res.data.data);
        });
      } catch (error) {
        console.error('Error fetching UOMs:', error);
      }
    },
    getCurrencies(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_currency`,
          params: params,
        }).then(res => {
          context.commit('setCurrenciesData', res.data.data);
        });
      } catch (error) {
        console.error('Error fetching currencies:', error);
      }
    },
    getTruckTypes(context, params = {}) {
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_truck_types`,
          params: params,
        }).then(res => {
          context.commit('setTruckTypesData', res.data.data);
        });
      } catch (error) {
        console.error('Error fetching currencies:', error);
      }
    },
    // preloaded Maintenance Datas
    getOUAccess(context) {
      context.commit('getOUAccess');
    },
    getActiveOU(context) {
      context.commit('getActiveOU');
    },
    resetState(context) {
      context.commit('resetState');
    },

    // Booking v2 actions
    getUOM2(context, params = {}) {
      if (!params.search && context.state.uomData2 && context.state.uomData2.length > 0) return;
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_uom2`,
          params: params
        }).then(res => {
          context.commit('setUomData2', res.data);
        }).catch((error) => {
          console.error('Error fetching UOM2 data:', error);
        });
      } catch (error) {
        console.error('Error in getUOM2 action:', error);
      }
    },
    getTruckType2(context, params = {}) {
      if (!params.search && context.state.truckTypeData2 && context.state.truckTypeData2.length > 0) return;
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_truck_type2`,
          params: params
        }).then(res => {
          context.commit('setTruckTypeData2', res.data);
        }).catch((error) => {
          console.error('Error fetching truck type2 data:', error);
        });
      } catch (error) {
        console.error('Error in getTruckType2 action:', error);
      }
    },
    getCommodities(context, params = {}) {
      if (!params.search && context.state.commodityData && context.state.commodityData.length > 0) return;
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_commodity`,
          params: params
        }).then(res => {
          context.commit('setCommodity', res.data.data || res.data);
        }).catch((error) => {
          console.error('Error fetching commodities:', error);
        });
      } catch (error) {
        console.error('Error in getCommodities action:', error);
      }
    },
    getCsr(context, params = {}) {
      if (!params.search && context.state.csrData && context.state.csrData.length > 0) return;
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_csr_list`,
          params: params
        }).then(res => {
          context.commit('setCsrData', res.data.data || res.data);
        }).catch((error) => {
          console.error('Error fetching CSR data:', error);
        });
      } catch (error) {
        console.error('Error in getCsr action:', error);
      }
    },
    getCsr2(context, params = {}) {
      const search = params.search || '';
      if (!search && context.state.csrData2 && context.state.csrData2.length > 0) return;
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_csr_refs2`,
          params: params
        }).then(res => {
          context.commit('setCsrData2', res.data);
        }).catch((error) => {
          console.error('Error fetching CSR refs2 data:', error);
        });
      } catch (error) {
        console.error('Error in getCsr2 action:', error);
      }
    },
    getAirportCodes2(context, params = {}) {
      const search = params.search || '';
      if (!search && context.state.airportCodesData2 && context.state.airportCodesData2.length > 0) return;
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_airport_codes2`,
          params: params
        }).then(res => {
          context.commit('setAirportCodesData2', res.data);
        }).catch((error) => {
          console.error('Error fetching airport codes2 data:', error);
        });
      } catch (error) {
        console.error('Error in getAirportCodes2 action:', error);
      }
    },
    getCustomers2(context, params = {}) {
      const search = params.search || '';
      if (!search && context.state.customersData2 && context.state.customersData2.length > 0) {
        const firstCustomer = context.state.customersData2[0];
        if (firstCustomer && 'address_street' in firstCustomer) return Promise.resolve(context.state.customersData2);
      }
      try {
        return axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_customers2`,
          params: params
        }).then(res => {
          context.commit('setCustomersData2', res.data);
          return res.data;
        }).catch((error) => {
          console.error('Error fetching customers2 data:', error);
          throw error;
        });
      } catch (error) {
        console.error('Error in getCustomers2 action:', error);
        return Promise.reject(error);
      }
    },
    getCurrencies2(context, params = {}) {
      if (!params.search && context.state.currenciesData2 && context.state.currenciesData2.length > 0) return;
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_currencies2`,
          params: params
        }).then(res => {
          context.commit('setCurrenciesData2', res.data);
        }).catch((error) => {
          console.error('Error fetching currencies2 data:', error);
        });
      } catch (error) {
        console.error('Error in getCurrencies2 action:', error);
      }
    },
    getChargeGroups2(context, params = {}) {
      if (!params.search && context.state.chargeGroupsData2 && context.state.chargeGroupsData2.length > 0) return;
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_charge_groups2`,
          params: params
        }).then(res => {
          context.commit('setChargeGroupsData2', res.data);
        }).catch((error) => {
          console.error('Error fetching charge groups2 data:', error);
        });
      } catch (error) {
        console.error('Error in getChargeGroups2 action:', error);
      }
    },
    getChargeList2(context, params = {}) {
      if (!params.search && context.state.chargeListData2 && context.state.chargeListData2.length > 0) return;
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_charge_list2`,
          params: params
        }).then(res => {
          context.commit('setChargeListData2', res.data);
        }).catch((error) => {
          console.error('Error fetching charge list2 data:', error);
        });
      } catch (error) {
        console.error('Error in getChargeList2 action:', error);
      }
    },
    getAirlineData2(context, params = {}) {
      const search = params.search || '';
      if (!search && context.state.airlineData2 && context.state.airlineData2.length > 0) return;
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_airlines2`,
          params: params
        }).then(res => {
          if (search) {
            // Merge search results so the full list is never replaced by a subset
            context.commit('pushAirlineData2', res.data);
          } else {
            context.commit('setAirlineData2', res.data);
          }
        }).catch((error) => {
          console.error('Error fetching airline data2:', error);
        });
      } catch (error) {
        console.error('Error in getAirlineData2 action:', error);
      }
    },
    getForExportAirOptions(context, params = {}) {
      try {
        return axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/for_export_air_options`,
          params: params
        }).then(res => {
          const data = res.data?.data || {};
          if (data.currencies || data.currenciesData2) {
            context.commit('setCurrenciesData2', data.currencies || data.currenciesData2);
          }
          if (data.truck_types || data.truckTypeData2) {
            context.commit('setTruckTypeData2', data.truck_types || data.truckTypeData2);
          }
          if (data.airlines || data.airlineData2) {
            context.commit('setAirlineData2', data.airlines || data.airlineData2);
          }
          if (data.incoterms || data.incotermData) {
            context.commit('setIncoterm', data.incoterms || data.incotermData);
          }
          if (data.departments || data.departmentData) {
            context.commit('setDepartmentList', data.departments || data.departmentData);
          }
          if (data.charge_list || data.chargeListData2) {
            context.commit('setChargeListData2', data.charge_list || data.chargeListData2);
          }
          if (data.uom || data.uomData2 || data.get_uom2) {
            context.commit('setUomData2', data.uom || data.uomData2 || data.get_uom2);
          }
          if (data.commodities || data.commodityData || data.get_commodity) {
            context.commit('setCommodity', data.commodities || data.commodityData || data.get_commodity);
          }
          if (data.charge_groups || data.chargeGroupsData2 || data.get_charge_groups2) {
            context.commit('setChargeGroupsData2', data.charge_groups || data.chargeGroupsData2 || data.get_charge_groups2);
          }
          if (data.weight_breaks || data.wtbData2 || data.get_weight_breaks2) {
            context.commit('setWtbData2', data.weight_breaks || data.wtbData2 || data.get_weight_breaks2);
          }
          if (data.airport_codes || data.airportCodesData2 || data.get_airport_codes2) {
            context.commit('setAirportCodesData2', data.airport_codes || data.airportCodesData2 || data.get_airport_codes2);
          }
          return res.data;
        }).catch((error) => {
          console.error('Error fetching for_export_air_options data:', error);
          throw error;
        });
      } catch (error) {
        console.error('Error in getForExportAirOptions action:', error);
        return Promise.reject(error);
      }
    },
    getForExportSeaOptions(context, params = {}) {
      try {
        return axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/for_export_sea_options`,
          params: params
        }).then(res => {
          const data = res.data?.data || {};
          if (data.currencies || data.currenciesData2) {
            context.commit('setCurrenciesData2', data.currencies || data.currenciesData2);
          }
          if (data.truck_types || data.truckTypeData2) {
            context.commit('setTruckTypeData2', data.truck_types || data.truckTypeData2);
          }
          if (data.incoterms || data.incotermData) {
            context.commit('setIncoterm', data.incoterms || data.incotermData);
          }
          if (data.departments || data.departmentData) {
            context.commit('setDepartmentList', data.departments || data.departmentData);
          }
          if (data.charge_list || data.chargeListData2) {
            context.commit('setChargeListData2', data.charge_list || data.chargeListData2);
          }
          if (data.uom || data.uomData2 || data.get_uom2) {
            context.commit('setUomData2', data.uom || data.uomData2 || data.get_uom2);
          }
          if (data.charge_groups || data.chargeGroupsData2 || data.get_charge_groups2) {
            context.commit('setChargeGroupsData2', data.charge_groups || data.chargeGroupsData2 || data.get_charge_groups2);
          }
          return res.data;
        }).catch((error) => {
          console.error('Error fetching for_export_sea_options data:', error);
          throw error;
        });
      } catch (error) {
        console.error('Error in getForExportSeaOptions action:', error);
        return Promise.reject(error);
      }
    },
    getWtbData2(context, params = {}) {
      if (!params.search && context.state.wtbData2 && context.state.wtbData2.length > 0) return;
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_weight_breaks2`,
          params: params
        }).then(res => {
          context.commit('setWtbData2', res.data);
        }).catch((error) => {
          console.error('Error fetching weight breaks2 data:', error);
        });
      } catch (error) {
        console.error('Error in getWtbData2 action:', error);
      }
    },
    getContainerType2(context, params = {}) {
      if (!params.search && context.state.containerTypeData2 && context.state.containerTypeData2.length > 0) return;
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_container_types2`,
          params: params
        }).then(res => {
          context.commit('setContainerTypeData2', res.data);
        }).catch((error) => {
          console.error('Error fetching container types2 data:', error);
        });
      } catch (error) {
        console.error('Error in getContainerType2 action:', error);
      }
    },
    getTransType2(context, params = {}) {
      if (!params.search && context.state.transTypeData2 && context.state.transTypeData2.length > 0) return;
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_transaction_types2`,
          params: params
        }).then(res => {
          context.commit('setTransTypeData2', res.data);
        }).catch((error) => {
          console.error('Error fetching transaction types2 data:', error);
        });
      } catch (error) {
        console.error('Error in getTransType2 action:', error);
      }
    },
    getLoadType2(context, params = {}) {
      if (!params.search && context.state.loadTypeData2 && context.state.loadTypeData2.length > 0) return;
      try {
        axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/get_load_types2`,
          params: params
        }).then(res => {
          context.commit('setLoadTypeData2', res.data);
        }).catch((error) => {
          console.error('Error fetching load types2 data:', error);
        });
      } catch (error) {
        console.error('Error in getLoadType2 action:', error);
      }
    },

    // Ocean freight rates maintenance
    getShippingLines(context, params = {}) {
      if (!params.search && context.state.shippingLineOptions && context.state.shippingLineOptions.length > 0) return;
      try {
        return axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/shipping-lines`,
          params: { per_page: 'ALL', ...params }
        }).then(res => {
          context.commit('setShippingLineOptions', res.data.data);
        }).catch((error) => {
          console.error('Error fetching shipping lines:', error);
        });
      } catch (error) {
        console.error('Error in getShippingLines action:', error);
      }
    },
    getContainerTypeOptions(context, params = {}) {
      if (!params.search && context.state.containerTypeOptions && context.state.containerTypeOptions.length > 0) return;
      try {
        return axios({
          method: 'get',
          url: `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/container-types`,
          params: params
        }).then(res => {
          context.commit('setContainerTypeOptions', res.data.data);
        }).catch((error) => {
          console.error('Error fetching container types:', error);
        });
      } catch (error) {
        console.error('Error in getContainerTypeOptions action:', error);
      }
    },

    getBulkStates(context, statesArray) {
      if (!statesArray || statesArray.length === 0) return Promise.resolve();

      // Only request states that are not yet loaded in Vuex store
      const missingStates = statesArray.filter(key => {
        const val = context.state[key];
        return !val || (Array.isArray(val) && val.length === 0);
      });

      if (missingStates.length === 0) {
        return Promise.resolve();
      }

      const commitStates = (data) => {
        if (!data) return;
        if (data.origDestAirData !== undefined) context.commit('setOrigDestAir', data.origDestAirData);
        if (data.origDestDomesticData !== undefined) context.commit('setOrigDestDomestic', data.origDestDomesticData);
        if (data.origDestSeaData !== undefined) context.commit('setOrigDestSea', data.origDestSeaData);
        if (data.commodityData !== undefined) context.commit('setCommodity', data.commodityData);
        if (data.serviceData !== undefined) context.commit('setService', data.serviceData);
        if (data.customerData !== undefined) context.commit('setCustomer', data.customerData);
        if (data.consigneeData !== undefined) context.commit('setConsignee', data.consigneeData);
        if (data.shipperData !== undefined) context.commit('setShipper', data.shipperData);
        if (data.incotermData !== undefined) context.commit('setIncoterm', data.incotermData);
        if (data.currencyData !== undefined) context.commit('setCurrencyData', data.currencyData);
        if (data.satellitesData !== undefined) context.commit('setSatellites', data.satellitesData);
        if (data.stationsData !== undefined) context.commit('setStationsData', data.stationsData);
        if (data.agentData !== undefined) context.commit('setAgent', data.agentData);
        if (data.wtbData !== undefined) context.commit('setWtb', data.wtbData);
        if (data.airlineData !== undefined) context.commit('setAirline', data.airlineData);
        if (data.rateTypesData !== undefined) context.commit('setRateTypes', data.rateTypesData);
        if (data.uomData !== undefined) context.commit('setUOM', data.uomData);
        if (data.designationData !== undefined) context.commit('setDesignation', data.designationData);
        if (data.areaData !== undefined) context.commit('setAreaData', data.areaData);
        if (data.citiesData !== undefined) context.commit('setCities', data.citiesData);
        if (data.airlineClassData !== undefined) context.commit('setAirlineClass', data.airlineClassData);
        if (data.truckTypeData !== undefined) context.commit('setTruckTypeList', data.truckTypeData);
        if (data.departmentData !== undefined) context.commit('setDepartmentList', data.departmentData);
        if (data.tripTypeData !== undefined) context.commit('setTripType', data.tripTypeData);
        if (data.chargeListData2 !== undefined) context.commit('setChargeListData2', data.chargeListData2);
        if (data.chargeListData !== undefined) context.commit('setChargeListData2', data.chargeListData);
        if (data.containerTypeData !== undefined) context.commit('setContainerType', data.containerTypeData);
        if (data.containerTypesData !== undefined) context.commit('setContainerType', data.containerTypesData);
        if (data.warehouseData !== undefined) context.commit('setWarehouses', data.warehouseData);
        if (data.warehousesData !== undefined) context.commit('setWarehouses', data.warehousesData);
        if (data.companiesData !== undefined) context.commit('setCompanies', data.companiesData);
        if (data.companyData !== undefined) context.commit('setCompanies', data.companyData);
        if (data.shippingLineOptions !== undefined) context.commit('setShippingLineOptions', data.shippingLineOptions);
        if (data.shippingLinesData !== undefined) context.commit('setShippingLineOptions', data.shippingLinesData);
        if (data.countryData !== undefined) context.commit('setCountryData', data.countryData);
      };

      try {
        return axios.post(`${process.env.VUE_APP_AUTHDOMAIN}/api/v1/fetch_bulk_states`, {
          states: missingStates
        }).then(res => {
          commitStates(res.data?.data);
          return res;
        }).catch(async error => {
          if (error?.response?.status === 429) {
            console.warn('Bulk states request rate-limited; skipping per-state retry to avoid amplifying it.', error);
            return null;
          }
          console.warn('Bulk states request failed. Retrying missing states individually to isolate server error...', error);
          for (const stateName of missingStates) {
            // Skip if state was already populated
            const currentVal = context.state[stateName];
            if (currentVal && Array.isArray(currentVal) && currentVal.length > 0) continue;

            try {
              const res = await axios.post(`${process.env.VUE_APP_AUTHDOMAIN}/api/v1/fetch_bulk_states`, {
                states: [stateName]
              });
              commitStates(res.data?.data);
            } catch (singleErr) {
              console.error(`❌ Server error fetching individual state '${stateName}':`, singleErr);
            }
          }
          return null;
        });
      } catch (error) {
        console.error('Error in getBulkStates action:', error);
      }
    },
    clearCsrConversionData(context) {
      context.commit('clearCsrConversionData');
    },
    setAirlineSurchargesIsProcessed(context, data) {
      context.commit('setAirlineSurchargesIsProcessed', data);
    },
    setSeaSurchargesIsProcessed(context, data) {
      context.commit('setSeaSurchargesIsProcessed', data);
    },
  },
  // NOTE: end Action
  mutations: {
    setMenus(state, menus) {
      localStorage.setItem('storedMenu', JSON.stringify(menus));
      state.menus = menus;
    },
    getMenus(state) {
      const storedMenu = localStorage.getItem('storedMenu');
      if (storedMenu) {
        state.menus = JSON.parse(storedMenu);
      }
    },
    setRoles(state, roles) {
      localStorage.setItem('userRoles', JSON.stringify(roles));
      state.userRoles = roles;
    },
    getRoles(state) {
      const storedRoles = localStorage.getItem('userRoles');
      if (storedRoles) {
        state.userRoles = JSON.parse(storedRoles);
      }
    },
    setRules(state, rules) {
      localStorage.setItem('rules', JSON.stringify(rules));
      state.rules = rules;
    },
    getRules(state) {
      const rules = localStorage.getItem('rules');
      if (rules) {
        state.rules = JSON.parse(rules);
      }
    },
    setOUAccess(state, ous) {
      localStorage.setItem('ouAccess', JSON.stringify(ous));
      // console.log(roles);
      state.ouAccess = ous;
    },
    getOUAccess(state) {
      const storedOU = localStorage.getItem('ouAccess');
      if (storedOU) {
        state.ouAccess = JSON.parse(storedOU);
      }
    },
    setActiveOU(state, ou) {
      localStorage.setItem('activeOU', ou);
      state.activeOU = ou;
    },
    getActiveOU(state) {
      const storedOU = localStorage.getItem('activeOU');
      if (storedOU) {
        state.activeOU = storedOU;
      }
    },
    setPermissions(state, permissions) {
      localStorage.setItem('permissions', JSON.stringify(permissions));
      // console.log(roles);
      state.permissions = permissions;
    },
    getPermissions(state) {
      const permissions = localStorage.getItem('permissions');
      if (permissions) {
        state.permissions = JSON.parse(permissions);
      }
    },
    setunitmeasureData(state, data) {
      state.unitmeasureData = data;
    },
    setUserId(state, Id) {
      state.userId = Id;
    },
    setSalesOfficerDetails(state, data) {
      state.salesOfficerDetails = data;
    },
    setOrigDestAir(state, data) {
      state.origDestAirData = data || [];
    },
    setSatellites(state, data) {
      state.satellitesData = data || [];
    },
    setStationsData(state, data) {
      state.stationsData = data || [];
    },
    setOrigDestDomestic(state, data) {
      state.origDestDomesticData = data || [];
    },
    setOrigDestSea(state, data) {
      state.origDestSeaData = data || [];
    },
    setUOM(state, data) {
      state.uomData = data || [];
    },
    setRateTypes(state, data) {
      state.rateTypesData = data || [];
    },
    setCurrencyData(state, data) {
      state.currencyData = data || [];
    },
    setCommodity(state, data) {
      state.commodityData = data || [];
    },
    setService(state, data) {
      state.serviceData = data || [];
    },
    setCustomer(state, data) {
      state.customerData = data || [];
    },
    setConsignee(state, data) {
      state.consigneeData = data || [];
    },
    setDesignation(state, data) {
      state.designationData = data || [];
    },
    setAreaData(state, data) {
      state.areaData = data || [];
    },
    setCities(state, data) {
      state.citiesData = data || [];
    },
    setShipper(state, data) {
      state.shipperData = data || [];
    },
    setAgent(state, data) {
      state.agentData = data || [];
    },
    setIncoterm(state, data) {
      state.incotermData = data || [];
    },
    setAirlineClass(state, data) {
      state.airlineClassData = data || [];
    },
    setWtb(state, data) {
      state.wtbData = data || [];
    },
    setAirline(state, data) {
      state.airlineData = data || [];
    },
    setCompanies(state, data) {
      state.companiesData = data;
    },
    setWarehouses(state, data) {
      state.warehousesData = data || [];
      state.warehouseData = data || [];
    },
    setWarehouseData(state, data) {
      state.warehousesData = data || [];
      state.warehouseData = data || [];
    },
    setChargeHeaders(state, data) {
      state.chargeHeadersData = data;
    },
    setPeriods(state, data) {
      state.periodsData = data;
    },
    setRates(state, data) {
      state.ratesData = data;
    },
    setUomsData(state, data) {
      state.uomsData = data;
    },
    setCurrenciesData(state, data) {
      state.currenciesData = data;
    },
    setTruckTypesData(state, data) {
      state.truckTypesData = data;
    },
    addLoadMore(state, data) {
      state.loadMore.push(data);
    },
    setDepartmentList(state, data) {
      state.departmentData = data;
    },
    removeLoadMore(state, data) {
      let index = state.loadMore.indexOf(data);
      while (index !== -1) {
        state.loadMore.splice(index, 1);
        index = state.loadMore.indexOf(data);
      }
    },
    setActiveBookingId(state, id) {
      state.activeBookingId = id;
      if (id) {
        sessionStorage.setItem('activeBookingId', id);
      } else {
        sessionStorage.removeItem('activeBookingId');
      }
    },
    setTruckTypeList(state, data) {
      state.truckTypeData = data || [];
    },
    setTripType(state, data) {
      state.tripTypeData = data || [];
    },
    setContainerType(state, data) {
      state.containerTypeData = data || [];
    },
    setShippingLineOptions(state, data) {
      state.shippingLineOptions = data || [];
    },
    setContainerTypeOptions(state, data) {
      state.containerTypeOptions = data || [];
    },
    setCountryData(state, data) {
      state.countryData = data || [];
    },
    resetState(state) {
      state.origDestAirData = [];
      state.origDestDomesticData = [];
      state.origDestSeaData = [];
      state.commodityData = [];
      state.serviceData = [];
      state.customerData = [];
      state.consigneeData = [];
      state.shipperData = [];
      state.incotermData = [];
      state.currencyData = [];
      state.satellitesData = [];
      state.stationsData = [];
      state.agentData = [];
      state.wtbData = [];
      state.unitmeasureData = [];
      state.airlineData = [];
      state.rateTypesData = [];
      state.uomData = [];
      state.designationData = [];
      state.areaData = [];
      state.airlineClassData = [];
      state.departmentData = [];
      state.tripTypeData = [];
      state.truckTypesData = [];
      state.shippingLineOptions = [];
      state.containerTypeOptions = [];
      state.countryData = [];
    },

    // Booking v2 mutations
    setUomData2(state, data) {
      state.uomData2 = data || [];
    },
    setTruckTypeData2(state, data) {
      state.truckTypeData2 = data || [];
    },
    setCsrData(state, data) {
      state.csrData = data || [];
    },
    setCsrData2(state, data) {
      state.csrData2 = data || [];
    },
    setAirportCodesData2(state, data) {
      state.airportCodesData2 = data || [];
    },
    pushAirportCodesData2(state, data) {
      if (!Array.isArray(data)) data = [data];
      data.forEach(newItem => {
        const index = state.airportCodesData2.findIndex(item => item.airport_code === newItem.airport_code);
        if (index === -1) {
          state.airportCodesData2.push(newItem);
        } else {
          state.airportCodesData2.splice(index, 1, newItem);
        }
      });
    },
    setCustomersData2(state, data) {
      state.customersData2 = data || [];
    },
    pushCustomersData2(state, data) {
      if (!Array.isArray(data)) data = [data];
      data.forEach(newItem => {
        if (!newItem || !newItem.customer_name) return;
        const index = state.customersData2.findIndex(item => item.customer_name === newItem.customer_name);
        if (index === -1) {
          state.customersData2.push(newItem);
        } else {
          state.customersData2.splice(index, 1, newItem);
        }
      });
    },
    setCurrenciesData2(state, data) {
      state.currenciesData2 = data || [];
    },
    setChargeGroupsData2(state, data) {
      state.chargeGroupsData2 = data || [];
    },
    setChargeListData2(state, data) {
      state.chargeListData2 = data || [];
    },
    setAirlineData2(state, data) {
      state.airlineData2 = data || [];
    },
    pushAirlineData2(state, data) {
      if (!Array.isArray(data)) data = data ? [data] : [];
      data.forEach(newItem => {
        if (!newItem || !newItem.air_code) return;
        const index = state.airlineData2.findIndex(item => item.air_code === newItem.air_code);
        if (index === -1) {
          state.airlineData2.push(newItem);
        } else {
          state.airlineData2.splice(index, 1, newItem);
        }
      });
    },
    setWtbData2(state, data) {
      state.wtbData2 = data || [];
    },
    setContainerTypeData2(state, data) {
      state.containerTypeData2 = data || [];
    },
    setTransTypeData2(state, data) {
      state.transTypeData2 = data || [];
    },
    setLoadTypeData2(state, data) {
      state.loadTypeData2 = data || [];
    },
    setCsrConversionData(state, data) {
      state.csrConversionData = data;
    },
    clearCsrConversionData(state) {
      state.csrConversionData = null;
    },
    setAirlineSurchargesIsProcessed(state, data) {
      state.airlineSurchargesIsProcessed = data;
    },
    setSeaSurchargesIsProcessed(state, data) {
      state.seaSurchargesIsProcessed = data;
    },
  },
  plugins: [persistedState.plugin],

});

export default store;
